// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  GroupType,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  getImageUrl,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import GroupFields from '../../fields/GroupFields';
import ThQty from '../../common/ThQty';
import ThSort from '../../common/ThSort';

// Helpers
import config from '../../../config';

// Services
import Services from '../../../services';

// Types
type $Props = $Translate;

type $State = {
  data: Array<$Model<'Group'>>;
  dataSortConfig: null | string;
  loading: boolean;
};

const pageId = adminPages.adminGroupsPage;

class AdminGroupsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      dataSortConfig: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Group') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminGroup();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async sortData(newDataSortConfig: string): Promise<void> {
    const {
      data,
    } = this.state;

    const [key, direction] = newDataSortConfig.split(':');

    await this.setStateAsync({
      loading: true,
    });

    const sortedData = _.orderBy(
      data,
      [key],
      [direction === 'desc' ? 'desc' : 'asc'],
    );

    await this.setStateAsync({
      data: sortedData,
      dataSortConfig: newDataSortConfig,
      loading: false,
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'Group'> = {
      CrewQuantity: 0,
      Image: null,
      Name: '',
      Type: GroupType.cruiser,
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <GroupFields item={newItem} />
              )}
              id={'create-group-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'Group'>>({
                    ...newItem,
                    ...values,
                    CrewQuantity: numberValue(values.CrewQuantity),
                  });

                  await Services.Api.upsertAdminGroup(params);
                },
              })}
              subTitle={t('New')}
              title={t('Group')}
            >
              <Button
                color={'info'}
                id={'create-group-button'}
                size={'sm'}
                title={t('Add new group')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
          )}
          title={t('Groups')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'Group'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <GroupFields item={dataItem} />
          )}
          id={`update-group-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Group'>>({
                ...dataItem,
                ...values,
                CrewQuantity: numberValue(values.CrewQuantity),
              });

              await Services.Api.upsertAdminGroup(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Group')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-group-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-group-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminGroup(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Group')}
        >
          <Button
            color={'danger'}
            id={`delete-group-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
      dataSortConfig,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Image')}</th>
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Name')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Name'}
            />
            <ThSort
              dataSortConfig={dataSortConfig}
              label={t('Type')}
              onSort={(newDataSortConfig: string) => this.sortData(newDataSortConfig)}
              sortKey={'Type'}
            />
            <th>{t('Crew quantity')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Group'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.Name}</td>
              <td>{dataItem.Type}</td>
              <td>{dataItem.CrewQuantity || ''}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminGroupsPageView;
